import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  SiHtml5, SiCss3, SiJavascript, SiReact, SiTailwindcss,
  SiPhp, SiFigma, SiAdobexd, SiGit, SiGithub,
} from 'react-icons/si';
import { TbBrandFramerMotion } from 'react-icons/tb';
import { FiLayers, FiRefreshCw, FiGrid, FiSmartphone } from 'react-icons/fi';
import ScrollGrid from './ScrollGrid';
import FlickerText from './FlickerText';
import './Skills.css';

const skillCategories = [
  {
    number: '01',
    title: 'LANGUAGES & FRAMEWORKS',
    skills: [
      { name: 'HTML5', icon: <SiHtml5 />, color: '#e34f26' },
      { name: 'CSS3', icon: <SiCss3 />, color: '#1572b6' },
      { name: 'JavaScript (ES6+)', icon: <SiJavascript />, color: '#f7df1e' },
      { name: 'React.js', icon: <SiReact />, color: '#61dafb' },
      { name: 'Tailwind CSS', icon: <SiTailwindcss />, color: '#06b6d4' },
      { name: 'PHP', icon: <SiPhp />, color: '#777bb4' },
    ],
  },
  {
    number: '02',
    title: 'DESIGN & UX',
    skills: [
      { name: 'Figma', icon: <SiFigma />, color: '#f24e1e' },
      { name: 'Adobe XD', icon: <SiAdobexd />, color: '#ff61f6' },
      { name: 'Framer Motion', icon: <TbBrandFramerMotion />, color: '#0055ff' },
      { name: 'UI Principles', icon: <FiLayers />, color: '#9b6aef' },
      { name: 'Prototyping', icon: <FiRefreshCw />, color: '#7c6aef' },
    ],
  },
  {
    number: '03',
    title: 'TOOLS & PLATFORMS',
    skills: [
      { name: 'Git', icon: <SiGit />, color: '#f05032' },
      { name: 'GitHub', icon: <SiGithub />, color: '#ffffff' },
      { name: 'Flexbox/Grid', icon: <FiGrid />, color: '#06b6d4' },
      { name: 'Responsive Design', icon: <FiSmartphone />, color: '#10b981' },
    ],
  },
];

const Skills = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.2 });

  return (
    <section className="skills" id="skills" ref={ref}>
      <ScrollGrid fadeVariant="fade-left" />
      <div className="container">
        <motion.div
          className="skills-header"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.3, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <span className="section-label">Expertise</span>
          <h2 className="section-big-title">
            <FlickerText text="TECH" isInView={isInView} /><br />
            <span className="outline"><FlickerText text="STACK" isInView={isInView} delay={0.3} /></span><span className="accent">.</span>
          </h2>
          <p className="section-description">
            My toolkit for building beautiful, functional, and accessible digital experiences.
          </p>
        </motion.div>

        {/* Category Blocks */}
        <div className="skills-categories">
          {skillCategories.map((category, catIndex) => (
            <motion.div
              className="skill-category-block"
              key={category.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.3, y: 20 }}
              transition={{ duration: 0.5, delay: 0.2 + catIndex * 0.15 }}
            >
              <div className="category-label">
                <span className="cat-num">{category.number}</span>
                <span className="cat-title">{category.title}</span>
              </div>
              <div className="category-skills-grid">
                {category.skills.map((skill) => (
                  <motion.div
                    className="skill-chip glass"
                    key={skill.name}
                    whileHover={{ borderColor: `${skill.color}50` }}
                    transition={{ duration: 0.2 }}
                  >
                    <span className="skill-chip-icon" style={{ color: skill.color }}>
                      {skill.icon}
                    </span>
                    <span className="skill-chip-name">{skill.name}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Skills;
